import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import { infoImageStyles } from "@stoneage/App/Widgets/InfoImage/InfoImage";
import Button from "@stoneage/MobiusOverrides/Button/Button";
import Clickable from "@stoneage/MobiusOverrides/Clickable/Clickable";
import LazyImage, { LazyImageProps } from "@stoneage/MobiusOverrides/LazyImage";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    gridContainer?: GridContainerProps;
    subCategoryItem?: GridItemProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    gridContainer: {
        gap: 22,
    },
    subCategoryItem: {
        width: [12, 6, 4, 4, 4],
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category?.subCategoryIds?.length) {
        return null;
    }

    return (
        <GridContainer {...styles.gridContainer}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem}>
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    const styles = infoImageStyles;

    return (
        <Clickable
            href={category.path}
            css={css`
                max-width: 100%;
                height: 100%;
            `}
        >
            <StyledWrapper className="info-image cat-info-image" {...styles.container} {...({ showCTA: true } as any)}>
                <StyledWrapper {...styles.imageContainer}>
                    <LazyImage
                        src={category.smallImagePath}
                        altText={category.imageAltText}
                        isCatalogImage={true}
                        height="160"
                        cloudinaryWidth="418"
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.innerTextWrapper} {...({ showCTA: true } as any)}>
                    <Typography {...styles.title} variant="h4" color="common.black">
                        {category.shortDescription}
                    </Typography>
                    {category?.htmlContent && (
                        <Typography {...styles.copy} color="text.main">
                            {parse(category.htmlContent, parserOptions)}
                        </Typography>
                    )}
                    <StyledWrapper
                        css={css`
                            margin-top: auto;
                        `}
                    >
                        <Button variant="primary" {...styles.button}>
                            {translate("View Products")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </Clickable>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
                isVisible: () => false,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
